import { Description } from '../Label/Description';
import React, { useId } from 'react';
import tw, { TwStyle } from 'twin.macro';
import { css } from '@emotion/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import Label from '@teetsh/app/src/components/Form/Label';
import { surfaceBrandColorAsTextColorStyle } from '@teetsh/app/src/components/colors';

export interface Option<T> {
  label: string;
  value: T;
  description?: React.ReactNode | string;
}

type Size = 'big' | 'small';

interface Props {
  id?: string;
  label?: string | React.ReactNode;
  description?: React.ReactNode;
  checked?: boolean;
  disabled?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
  size?: Size;
  name?: string;
}

const SIZE_STYLES: Record<
  Size,
  {
    checkbox: TwStyle;
    text: TwStyle;
    icon: TwStyle;
    container: TwStyle;
    spacing: TwStyle;
  }
> = {
  big: {
    checkbox: tw`size-5`,
    text: tw`text-base`,
    icon: tw`size-5`,
    container: tw`h-6`,
    spacing: tw`ml-2`,
  },
  small: {
    checkbox: tw`size-4`,
    text: tw`text-sm`,
    icon: tw`size-4`,
    container: tw`h-5`,
    spacing: tw`ml-1.5`,
  },
};

export function Checkbox({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
  size = 'small',
  name,
  ...props
}: Props) {
  const sizeStyles = SIZE_STYLES[size];
  const reactId = useId();
  const inputId = id ?? `checkbox-${reactId}`;

  return (
    <div
      css={[
        tw`relative flex items-start`,
        disabled && tw`opacity-40 cursor-not-allowed`,
      ]}
    >
      <div css={[tw`flex items-center relative`, sizeStyles.container]}>
        <input
          id={inputId}
          aria-describedby={id}
          aria-label={props['aria-label']}
          role="checkbox"
          name={name}
          type="checkbox"
          disabled={disabled}
          css={[
            surfaceBrandColorAsTextColorStyle,
            tw`border-neutral-moderate rounded-sm cursor-pointer`,
            sizeStyles.checkbox,
            tw`focus:ring-brand-moderate focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-offset-2 opacity-100 disabled:opacity-45`,
            css`
              background-image: none !important;
            `,
          ]}
          checked={checked}
          onChange={onChange}
        />
        {checked && (
          <CheckIcon
            tw="absolute text-white pointer-events-none inset-0 m-auto"
            css={sizeStyles.icon}
          />
        )}
      </div>
      {label && (
        <div css={sizeStyles.spacing}>
          <Label
            htmlFor={id}
            tw="cursor-pointer"
            css={[tw`cursor-pointer text-neutral-moderate`, sizeStyles.text]}
          >
            {label}
            {description && (
              <Description
                id={`${id}-description`}
                css={[tw`text-neutral-moderate font-normal`, sizeStyles.text]}
              >
                {description}
              </Description>
            )}
          </Label>
        </div>
      )}
    </div>
  );
}
